/**
 * What an indicator pane's Y axis is scaled to.
 *
 * The renderer has honoured this since it was written — `createYAxis` treats an
 * axis as pinned unless it is `autoRange: "always"` with no `visibleRange`, and
 * `fitYAxes` then leaves it alone — and qv-data has stored `auto_range`,
 * `visible_min` and `visible_max` all along. So this is a vocabulary for a
 * capability that already existed end to end, not a new one.
 *
 * **The range belongs to the axis, not to the indicator.** Two indicators can
 * share a pane, so "pin the RSI" really means "pin whatever draws on this axis".
 * Naming it after the pane is what stops the control lying the first time
 * somebody stacks two oscillators.
 */

import type { AxisInfo, ChartGroup, Pane } from "@qufolio/model";

/** The three answers, named for what the user is choosing. */
export type PaneScaleMode = "auto" | "natural" | "custom";

export interface PaneScale {
  mode: PaneScaleMode;
  /** Present for `natural` and `custom`; absent for `auto`. */
  range?: { min: number; max: number };
}

/**
 * Read an axis back as a choice.
 *
 * `natural` cannot be told from `custom` by looking at the axis alone — both are
 * a fixed range — so the caller passes the catalog's bounds and a range equal to
 * them reads as `natural`. That keeps the distinction where it belongs: the
 * contract stores what the axis *is*, and which button is lit is derived, the
 * same argument `matchTemplate` and `screenerPlacement` make. A stored mode
 * would be a second source of truth that a hand-edited range falsifies.
 */
export function paneScaleOf(
  axis: AxisInfo | undefined,
  natural?: { min: number; max: number },
): PaneScale {
  const range = axis?.visibleRange;
  const pinned =
    range?.min !== undefined && range?.max !== undefined && axis?.autoRange !== "always";
  if (!pinned) return { mode: "auto" };

  const bounds = { min: range.min!, max: range.max! };
  const isNatural =
    natural !== undefined && natural.min === bounds.min && natural.max === bounds.max;
  return { mode: isNatural ? "natural" : "custom", range: bounds };
}

/**
 * The axis a pane's scale control drives: its first.
 *
 * A pane may declare several axes, but an indicator pane in practice has one and
 * the plots reference it. Taking the first rather than searching by `axisRef`
 * keeps a hand-built pane whose plots carry no ref working, which is the same
 * fallback `addIndicator` uses when it looks for one.
 */
export function paneAxis(pane: Pane | undefined): AxisInfo | undefined {
  return pane?.axes?.[0];
}

/**
 * Write a scale onto a pane's axis.
 *
 * **A fixed range clears `autoRange`, and `auto` clears the range.** The two
 * contradict each other: `always` beside an explicit `visibleRange` means the
 * next render refits over whatever was written, so the axis would appear to
 * ignore the setting. `axisFor` already takes this care when an indicator is
 * added; this is the same rule applied to an edit.
 *
 * Returns the pane unchanged — by identity — when the pane has no axis to write
 * to, so a caller need not check first and React's memoisation still sees no
 * change.
 */
export function withPaneScale(pane: Pane, scale: PaneScale): Pane {
  const axes = pane.axes;
  if (!axes?.length) return pane;

  const next: AxisInfo =
    scale.mode === "auto"
      ? // Both cleared: an axis carrying a stale range would be re-pinned by the
        // renderer the moment `autoRange` was anything but `always`.
        { ...axes[0]!, autoRange: "always", visibleRange: undefined }
      : { ...axes[0]!, autoRange: "never", visibleRange: scale.range };

  return { ...pane, axes: [next, ...axes.slice(1)] };
}

/**
 * The same write on one axis named by id, wherever it is — an indicator's own
 * scale inside a pane it shares (a Rank on the price pane, owner 2026-10-06),
 * which is not the pane's first axis and so not what `withPaneScale` writes.
 * Returns `group` by identity when no pane holds that axis.
 */
export function setAxisScale(group: ChartGroup, axisId: string, scale: PaneScale): ChartGroup {
  let found = false;
  const panes = group.panes.map((pane) => {
    if (!pane.axes?.some((a) => a.id === axisId)) return pane;
    found = true;
    return {
      ...pane,
      axes: pane.axes.map((a) =>
        a.id !== axisId
          ? a
          : scale.mode === "auto"
            ? { ...a, autoRange: "always" as const, visibleRange: undefined }
            : { ...a, autoRange: "never" as const, visibleRange: scale.range },
      ),
    };
  });
  return found ? { ...group, panes } : group;
}

/** The same write, addressed through a chart group. */
export function setPaneScale(group: ChartGroup, paneId: string, scale: PaneScale): ChartGroup {
  return {
    ...group,
    panes: group.panes.map((p) => (p.id === paneId ? withPaneScale(p, scale) : p)),
  };
}
